import { useCallback, useEffect, useRef } from 'react';
import { AgentDot } from '../../../AgentDot';
import { Button, ScrollArea, Typography } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { MAESTRO_PILE_ID } from '../../../../lib/agent-pile';
import { agentControlProps } from '../../../../lib/agent-deselect';
import type { AgentSummary } from '../../../../lib/my-agents';
import type { useAgentName } from '../../../../lib/use-agent-name';
import { MaestroMark } from '../../MaestroMark';
import { YOUR_AGENTS_PAGE } from '../use-your-agents';
import styles from './MobileAgentCards.module.css';

// The roster on narrow (Figma `8509:36899`): every card fully open, side by side in a
// horizontally scrolling row. The wide screen's overlapping pile depends on hover and on
// width neither of which narrow has, so the same roster is laid out as a carousel — the
// Maestro first, then the team in the roster's own order, and nothing hidden behind a count.
//
// It still shares the genome's selection, and it drives it BOTH ways, exactly as native's
// carousel does: the LEADING card is the selection, so swiping the row continuously moves the
// genome's highlight from node to node; and picking an agent on the figure (or a card header)
// scrolls that card to the lead. Selection is a direct set, not a toggle — a toggle would fight
// the continuous scroll sync. The cards carry no selected styling; the card at the lead IS the
// feedback. The arrival reel is undisturbed because it never scrolls the row, so no leading-card
// selection fires until the user first swipes.

// The row's leading gutter (`--ds-space-24`), so the leading card is measured against the same
// inset `scroll-padding-inline-start` snaps it to.
const LEAD_INSET_PX = 24;
// Ignore scroll-driven selection while a programmatic scroll-to-lead is still gliding, so an
// intermediate card mid-glide can't steal the selection from the one we're scrolling to.
const PROGRAMMATIC_SCROLL_MS = 450;

interface Props {
  agents: AgentSummary[];
  maestro: ReturnType<typeof useAgentName>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function MobileAgentCards({ agents, maestro, selectedId, onSelect }: Props) {
  const { t } = useTranslation();
  const scroller = useRef<HTMLElement>(null);
  const cards = useRef(new Map<string, HTMLElement>());
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  // A selection that CAME FROM scrolling must not scroll again (it would fight the finger).
  const fromScroll = useRef(false);
  const programmaticUntil = useRef(0);
  const rafId = useRef<number | undefined>(undefined);

  // A selection made anywhere else — a genome node tap, a card-header tap — scrolls that card to
  // the lead. Skipped when the change came from the scroll itself.
  useEffect(() => {
    if (fromScroll.current) {
      fromScroll.current = false;
      return;
    }
    if (!selectedId) return;
    const card = cards.current.get(selectedId);
    if (!card) return;
    programmaticUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }, [selectedId]);

  // The card whose leading edge sits nearest the row's leading inset — direction-aware, so the
  // lead is the right edge in RTL.
  const leadingId = useCallback((): string | null => {
    const box = scroller.current;
    if (!box) return null;
    const rtl = getComputedStyle(box).direction === 'rtl';
    const boxRect = box.getBoundingClientRect();
    const anchor = rtl ? boxRect.right - LEAD_INSET_PX : boxRect.left + LEAD_INSET_PX;
    let best: string | null = null;
    let bestDist = Infinity;
    cards.current.forEach((el, id) => {
      const rect = el.getBoundingClientRect();
      const dist = Math.abs((rtl ? rect.right : rect.left) - anchor);
      if (dist < bestDist) {
        bestDist = dist;
        best = id;
      }
    });
    return best;
  }, []);

  const onScroll = useCallback(() => {
    if (Date.now() < programmaticUntil.current) return;
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = undefined;
      const id = leadingId();
      if (id && id !== selectedRef.current) {
        fromScroll.current = true;
        onSelect(id);
      }
    });
  }, [leadingId, onSelect]);

  useEffect(() => () => cancelAnimationFrame(rafId.current ?? 0), []);

  const register = (id: string) => (el: HTMLElement | null) => {
    if (el) cards.current.set(id, el);
    else cards.current.delete(id);
  };

  return (
    <ScrollArea ref={scroller} axis="horizontal" bar="hidden" className={styles.row} onScroll={onScroll}>
      <AgentCard
        cardRef={register(MAESTRO_PILE_ID)}
        id={MAESTRO_PILE_ID}
        maestro
        name={maestro.name}
        ctaName={maestro.short}
        role={t('agents.maestro.conductor')}
        description={t('agents.subtitle', { maestroAgentName: maestro.name })}
        href="/agents/personality"
        selected={selectedId === MAESTRO_PILE_ID}
        onSelect={onSelect}
      />
      {agents.map((agent) => {
        const id = agent.id || agent.name;
        return (
          <AgentCard
            key={id}
            cardRef={register(id)}
            id={id}
            maestro={false}
            name={agent.name}
            ctaName={agent.name}
            role={agent.title}
            description={agent.description}
            href={`/agents/${encodeURIComponent(id)}`}
            selected={selectedId === id}
            onSelect={onSelect}
          />
        );
      })}
    </ScrollArea>
  );
}

function AgentCard({
  cardRef,
  id,
  name,
  ctaName,
  role,
  description,
  href,
  maestro,
  selected,
  onSelect,
}: {
  cardRef: (el: HTMLElement | null) => void;
  id: string;
  name: string;
  ctaName: string;
  role?: string;
  description?: string;
  href: string;
  maestro: boolean;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();

  return (
    <article ref={cardRef} className={styles.card} {...agentControlProps}>
      {/* Pressing the card's own surface selects it on the genome; the CTA below navigates. */}
      <button
        type="button"
        className={styles.head}
        aria-pressed={selected}
        aria-label={t('agents.cardSelect', { name })}
        onClick={() => onSelect(id)}
      >
        <span className={styles.avatar} aria-hidden>
          {maestro ? <MaestroMark className={styles.maestroMark} /> : <AgentDot id={id} size={24} />}
        </span>
        <span className={styles.identity}>
          <Typography variant="h3" color="strong" component="span" noWrap>
            {name}
          </Typography>
          {role && (
            <Typography variant="p2" color="secondary" component="span">
              {role}
            </Typography>
          )}
        </span>
      </button>

      <span className={styles.divider} aria-hidden />

      {description && <p className={styles.description}>{description}</p>}

      <Button
        variant="outlined"
        size="large"
        fullWidth
        href={href}
        className={styles.cta}
        onClick={() =>
          logPageClickBi(
            { page_name: YOUR_AGENTS_PAGE, button_name: maestro ? 'view_maestro' : 'view_agent', answer: id },
            maestro ? undefined : { entity: { id, type: 'agent' } },
          )
        }
      >
        {t('agents.viewAgent', { name: ctaName })}
      </Button>
    </article>
  );
}
